import React, { useState, useRef, KeyboardEvent } from 'react';
import { VideoFeatures, SalesNotification } from '@/store/videoFeatures';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Bell, Repeat, Eye, Volume2, ImageIcon, Clock, MapPin, Zap, ChevronDown, ChevronRight, Type, Square, Users, Package, Link, X, Plus, Sparkles, Palette, MousePointerClick } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Label as RadixLabel } from '@radix-ui/react-label';
import { t } from '@/utils/i18n';
import { tw4 } from '@/utils/i18n/wave4';
import { getRedirectUrlAutofixSuffix, getRedirectUrlState } from '@/lib/redirect-url';
import { HeroCard } from '@/components/ui/HeroCard';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { HexColorPicker } from 'react-colorful';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

import { SALES_NOTIFICATION_PLATFORMS } from '@/lib/sales-notification-platforms';

// Position mapping
const POSITION_MAP: Record<string, { key: string; label: string }> = {
  'top-left': { key: 'topLeft', label: tw4('sn.pos.top-left') },
  'top-right': { key: 'topRight', label: tw4('sn.pos.top-right') },
  'bottom-left': { key: 'bottomLeft', label: tw4('sn.pos.bottom-left') },
  'bottom-right': { key: 'bottomRight', label: tw4('sn.pos.bottom-right') },
};

const VALID_POSITIONS = new Set(Object.keys(POSITION_MAP));

// Style presets
const STYLE_PRESETS = {
  host: {
    label: 'Padrão Host',
    description: 'Recomendado',
    bgColor: '#ffffff',
    textColor: '#333333',
    borderColor: '#eeeeee',
    borderRadius: 16,
    opacity: 100,
    boxShadow: true,
    padding: 'medium' as const,
  },
  minimal: {
    label: 'Minimal',
    description: 'Limpo e discreto',
    bgColor: '#ffffff',
    textColor: '#333333',
    borderColor: '#f1f5f9',
    borderRadius: 8,
    opacity: 80,
    boxShadow: false,
    padding: 'small' as const,
  },
  dark: {
    label: 'Dark',
    description: 'Fundo escuro',
    bgColor: '#1a1a2e',
    textColor: '#f1f5f9',
    borderColor: '#2d2d44',
    borderRadius: 12,
    opacity: 95,
    boxShadow: true,
    padding: 'medium' as const,
  },
  urgencia: {
    label: 'Urgência',
    description: 'Destaque forte',
    bgColor: '#ffffff',
    textColor: '#1a1a1a',
    borderColor: '#ef4444',
    borderRadius: 8,
    opacity: 100,
    boxShadow: true,
    padding: 'medium' as const,
  },
};

// Mock names for "Add 10 examples"
const MOCK_NAMES = ['Ana', 'João', 'Maria', 'Pedro', 'Lucas', 'Juliana', 'Carlos', 'Fernanda', 'Rafael', 'Beatriz'];
const MOCK_CITIES = ['São Paulo', 'Rio de Janeiro', 'Belo Horizonte', 'Brasília', 'Salvador', 'Curitiba', 'Fortaleza', 'Recife', 'Porto Alegre', 'Manaus'];

interface SalesNotificationConfigProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
}

// Collapsible group component matching FinalCountdown pattern — Glass + chevron menor
const AccordionGroup: React.FC<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}> = ({ icon, title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
        <CollapsibleTrigger className={cn(
          "flex items-center gap-3 w-full p-4 cursor-pointer group",
          isOpen && "rounded-t-[14px]"
        )}>
          <div className="w-9 h-9 rounded-[10px] bg-[#EEF4FF] flex items-center justify-center flex-shrink-0">
            {icon}
          </div>
          <span className="text-[13px] font-semibold text-[#0F172A] flex-1 text-left">{title}</span>
          <ChevronDown className={cn(
            "w-3.5 h-3.5 text-muted-foreground transition-transform duration-200",
            isOpen && "rotate-180"
          )} />
        </CollapsibleTrigger>
        <CollapsibleContent className="overflow-hidden data-[state=open]:animate-none data-[state=closed]:animate-none">
          <div className="px-4 pt-4 pb-4 space-y-4 border-t border-[#F1F5F9]">
            {children}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

// Inline Color Picker
const InlineColorPicker: React.FC<{
  value: string;
  onChange: (color: string) => void;
  label: string;
}> = ({ value, onChange, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="flex items-center justify-between py-2.5">
      <Label className="text-sm text-[#64748B]">{label}</Label>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="h-7 w-7 rounded-full border-2 border-[#E6E6F0] transition-all hover:scale-110 hover:border-[#4B7BFF]/50 shadow-sm"
            style={{ backgroundColor: value }}
          />
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="end" sideOffset={8}>
          <div className="space-y-2">
            <Label className="text-xs text-[#64748B]">{tw4('sn.colorCustom')}</Label>
            <Input
              type="color"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              className="h-10 w-32 cursor-pointer border-[#E6E6F0]"
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Chip input component for names/cities
const ChipInput: React.FC<{
  items: string[];
  onChange: (items: string[]) => void;
  placeholder: string;
  mockData: string[];
  mockLabel: string;
}> = ({ items, onChange, placeholder, mockData, mockLabel }) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      if (!items.includes(inputValue.trim())) {
        onChange([...items, inputValue.trim()]);
      }
      setInputValue('');
    }
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const addMockData = () => {
    const newItems = [...new Set([...items, ...mockData])];
    onChange(newItems);
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5 min-h-[36px] p-2 bg-[#F8FAFC] rounded-lg border border-[#E4E7EC]">
        {items.map((item, index) => (
          <span
            key={`${item}-${index}`}
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] bg-[#F1F5F9] text-[#0F172A] font-medium"
          >
            {item}
            <button
              type="button"
              onClick={() => removeItem(index)}
              className="hover:text-destructive transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={items.length === 0 ? placeholder : tw4('sn.addMore')}
          className="flex-1 min-w-[100px] bg-transparent text-[12px] outline-none placeholder:text-[#94A3B8]"
        />
      </div>
      <button
        type="button"
        onClick={addMockData}
        className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#4B7BFF] hover:text-[#4B7BFF]/80 transition-colors"
      >
        <Plus className="w-3 h-3" />
        {mockLabel}
      </button>
    </div>
  );
};

// Time preset chips — 32px + azul translúcido Host
const TimePresetChips: React.FC<{
  value: number;
  presets: number[];
  onChange: (v: number) => void;
  suffix?: string;
}> = ({ value, presets, onChange, suffix = 's' }) => (
  <div className="flex gap-1.5 mt-2">
    {presets.map((p) => (
      <button
        key={p}
        type="button"
        onClick={() => onChange(p)}
        className={cn(
          "h-8 rounded-full px-3.5 py-1.5 text-[11px] font-medium transition-all",
          value === p
            ? "border border-[#4B7BFF]/40 bg-[#4B7BFF]/[0.08] text-[#4B7BFF] font-semibold"
            : "border border-[#E4E7EC] bg-white text-[#475569] hover:border-[#4B7BFF]/30"
        )}
      >
        {p}{suffix}
      </button>
    ))}
  </div>
);

export const SalesNotificationConfig: React.FC<SalesNotificationConfigProps> = ({
  features,
  setFeature,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isUploading, setIsUploading] = useState(false);
  const customIconInputRef = useRef<HTMLInputElement>(null);
  const [namesMode, setNamesMode] = useState<'auto' | 'custom'>('custom');
  const [citiesMode, setCitiesMode] = useState<'auto' | 'custom' | 'none'>(() => {
    const sn = features.salesNotification;
    if (sn && sn.cities && sn.cities.length === 0 && sn.template && !sn.template.includes('{cidade}') && !sn.template.includes('{Cidade}')) {
      return 'none';
    }
    return 'custom';
  });
  
  const defaultSalesNotification: SalesNotification = {
    enabled: false,
    platform: 'perfectpay',
    showCount: 5,
    frequency: 30,
    interval: 5,
    startDelay: 10,
    animation: 'fade',
    position: 'bottom-right',
    soundEnabled: false,
    soundVolume: 50,
    fontSize: 'medium',
    style: {
      bgColor: '#ffffff',
      textColor: '#333333',
      borderColor: '#eeeeee',
      borderRadius: 16,
      opacity: 100,
      boxShadow: true,
      padding: 'medium'
    },
    type: 'simulated',
    template: '{nome} de {cidade} acabou de comprar {produto}',
    names: ['Ana', 'João', 'Maria', 'Pedro', 'Lucas'],
    cities: ['São Paulo', 'Rio de Janeiro', 'Belo Horizonte', 'Brasília', 'Salvador'],
    duration: 5,
    loop: true,
    iconSize: 'medium',
    showIcon: true
  };
  
  const salesNotification = features.salesNotification || {
    ...defaultSalesNotification,
    template: features.salesNotification?.template || '{nome} de {cidade} acabou de comprar {produto}'
  };

  const isCustomPlatform = salesNotification.platform === 'custom';

  // A notificação vira um <a href> no player: URL com esquema proibido é
  // recusada pelo backend e ignorada pelo player, então o aviso tem que
  // aparecer aqui. 'autofix' = sem esquema, será salva como https://.
  const redirectUrlState = getRedirectUrlState(salesNotification.redirectUrl);

  const updateSalesNotification = (updates: Partial<SalesNotification>) => {
    setFeature('salesNotification', {
      ...salesNotification,
      ...updates
    });
  };

  const handleEnabledChange = (enabled: boolean) => {
    updateSalesNotification({ enabled });
  };

  const handlePositionChange = (position: SalesNotification['position']) => {
    updateSalesNotification({ position });
  };

  const handleTemplateChange = (template: string) => {
    updateSalesNotification({ template });
    if (errors.template) {
      const newErrors = { ...errors };
      delete newErrors.template;
      setErrors(newErrors);
    }
  };

  const handlePlatformChange = (platform: SalesNotification['platform']) => {
    updateSalesNotification({ platform });
  };

  const handleIconUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      alert(t('salesNotification.error.iconSize'));
      return;
    }
    setIsUploading(true);
    try {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        updateSalesNotification({ customPlatformIcon: base64String });
        setIsUploading(false);
      };
      reader.onerror = () => {
        alert(t('salesNotification.error.iconUpload'));
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      alert(t('salesNotification.error.iconUpload'));
      setIsUploading(false);
    }
  };

  const applyStylePreset = (presetKey: keyof typeof STYLE_PRESETS) => {
    const preset = STYLE_PRESETS[presetKey];
    updateSalesNotification({
      style: {
        ...salesNotification.style,
        bgColor: preset.bgColor,
        textColor: preset.textColor,
        borderColor: preset.borderColor,
        borderRadius: preset.borderRadius,
        opacity: preset.opacity,
        boxShadow: preset.boxShadow,
        padding: preset.padding,
      }
    });
  };

  const getCurrentPreset = (): string => {
    const s = salesNotification.style;
    for (const [key, preset] of Object.entries(STYLE_PRESETS)) {
      if (
        s.bgColor === preset.bgColor &&
        s.textColor === preset.textColor &&
        s.borderRadius === preset.borderRadius &&
        s.boxShadow === preset.boxShadow
      ) return key;
    }
    return 'host';
  };

  // Fallback invalid position to bottom-right
  const effectivePosition = VALID_POSITIONS.has(salesNotification.position) 
    ? salesNotification.position 
    : 'bottom-right';
  
  // If position was invalid, update it
  React.useEffect(() => {
    if (!VALID_POSITIONS.has(salesNotification.position)) {
      updateSalesNotification({ position: 'bottom-right' });
    }
  }, [salesNotification.position]);

  // Readable position label
  const positionLabel = POSITION_MAP[effectivePosition]?.label || tw4('sn.fallbackPos');

  // Unified size from fontSize
  const currentSize = salesNotification.fontSize || 'medium';
    
  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Master Switch */}
      <div className="px-6 py-4">
        <HeroCard
          icon={<Bell className="w-full h-full" strokeWidth={2} />}
          title={tw4('sn.enable')}
          description={tw4('sn.enable.helper')}
          isActive={salesNotification.enabled}
          onToggle={handleEnabledChange}
        />
        
      </div>

      {salesNotification.enabled && (
        <div className="flex-1 flex flex-col px-6 pb-6 overflow-hidden">
          <Tabs defaultValue="basic" className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-3 h-11 bg-[#F1F5F9] p-1 rounded-xl shrink-0">
              <TabsTrigger 
                value="basic" 
                className="h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#4B7BFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm text-[#6B7280] transition-all duration-200"
              >
                {tw4('sn.tab.basic')}
              </TabsTrigger>
              <TabsTrigger 
                value="content" 
                className="h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#4B7BFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm text-[#6B7280] transition-all duration-200"
              >
                {tw4('sn.tab.content')}
              </TabsTrigger>
              <TabsTrigger 
                value="appearance" 
                className="h-9 rounded-lg text-[13px] font-medium data-[state=active]:bg-[#4B7BFF] data-[state=active]:text-white data-[state=active]:font-semibold data-[state=active]:shadow-sm text-[#6B7280] transition-all duration-200"
              >
                {tw4('sn.tab.appearance')}
              </TabsTrigger>
            </TabsList>
          
            {/* ========== TAB 1 — CONFIGURAÇÕES ========== */}
            <TabsContent value="basic" className="flex-1 m-0 overflow-y-auto premium-scroll mt-4">
              <div className="space-y-3 pb-6">
                
                {/* Grupo 1 — Velocidade (always open card) */}
                <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-[10px] bg-[#EEF4FF] flex items-center justify-center flex-shrink-0">
                      <Clock className="w-[18px] h-[18px] text-[#4B7BFF]" strokeWidth={2} />
                    </div>
                    <Label className="text-[14px] font-semibold text-[#1A1A1A]">{tw4('sn.speed.title')}</Label>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Atraso inicial — slider value chip */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm text-[#64748B]">{tw4('sn.speed.startDelay')}</Label>
                        <span className="text-[11px] font-medium text-[#4B7BFF] bg-[#4B7BFF]/[0.08] rounded-full px-2 py-0.5 min-w-[44px] text-center">
                          {salesNotification.startDelay || 0}s
                        </span>
                      </div>
                      <Slider
                        value={[salesNotification.startDelay || 0]}
                        min={0}
                        max={180}
                        step={5}
                        onValueChange={([value]) => updateSalesNotification({ startDelay: value })}
                      />
                      <TimePresetChips value={salesNotification.startDelay || 0} presets={[10, 20, 30]} onChange={(v) => updateSalesNotification({ startDelay: v })} />
                    </div>
                    
                    {/* Frequência — slider value chip */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm text-[#64748B]">{tw4('sn.speed.frequency')}</Label>
                        <span className="text-[11px] font-medium text-[#4B7BFF] bg-[#4B7BFF]/[0.08] rounded-full px-2 py-0.5 min-w-[44px] text-center">
                          {salesNotification.frequency}s
                        </span>
                      </div>
                      <Slider
                        value={[salesNotification.frequency]}
                        min={5}
                        max={300}
                        step={5}
                        onValueChange={([value]) => updateSalesNotification({ frequency: value })}
                      />
                      <TimePresetChips value={salesNotification.frequency} presets={[15, 30, 45]} onChange={(v) => updateSalesNotification({ frequency: v })} />
                    </div>
                    
                    {/* Duração — slider value chip */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm text-[#64748B]">{tw4('sn.speed.duration')}</Label>
                        <span className="text-[11px] font-medium text-[#4B7BFF] bg-[#4B7BFF]/[0.08] rounded-full px-2 py-0.5 min-w-[44px] text-center">
                          {salesNotification.duration}s
                        </span>
                      </div>
                      <Slider
                        value={[salesNotification.duration]}
                        min={1}
                        max={20}
                        step={1}
                        onValueChange={([value]) => updateSalesNotification({ duration: value })}
                      />
                      <TimePresetChips value={salesNotification.duration} presets={[3, 5, 7]} onChange={(v) => updateSalesNotification({ duration: v })} />
                    </div>
                  </div>
                </div>
                
                {/* Grupo — Posição (always open card) */}
                <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-[10px] bg-[#EEF4FF] flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-[18px] h-[18px] text-[#4B7BFF]" strokeWidth={2} />
                    </div>
                    <Label className="text-[14px] font-semibold text-[#1A1A1A]">{tw4('sn.position.title')}</Label>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(POSITION_MAP).map(([posKey, { label }]) => {
                      const isSelected = salesNotification.position === posKey;
                      return (
                        <button
                          key={posKey}
                          type="button"
                          onClick={() => handlePositionChange(posKey as SalesNotification['position'])}
                          className={cn(
                            "h-11 text-[11px] rounded-[10px] transition-all duration-200 ease-out font-medium px-2 focus:outline-none",
                            isSelected
                              ? "border-[#4B7BFF] border-2 bg-white text-[#4B7BFF]"
                              : "bg-white text-[#64748B] border border-[#E4E7EC] hover:border-[#4B7BFF]/30 hover:text-[#4B7BFF]"
                          )}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                
                {/* Grupo 2 — Loop (Collapsible) */}
                <AccordionGroup
                  icon={<Repeat className="w-[18px] h-[18px] text-[#4B7BFF]" strokeWidth={2} />}
                  title={tw4('sn.loop.title')}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <Label htmlFor="loop-toggle" className="text-sm font-medium text-[#1A1A1A] cursor-pointer">
                        {tw4('sn.loop.title')}
                      </Label>
                      <p className="text-[12px] text-muted-foreground mt-0.5">
                        {tw4('sn.loop.desc')}
                      </p>
                    </div>
                    <Switch
                      id="loop-toggle"
                      checked={salesNotification.loop !== false}
                      onCheckedChange={(loop) => updateSalesNotification({ loop })}
                    />
                  </div>
                </AccordionGroup>
                
                {/* Grupo 3 — Interação (Collapsible) */}
                <AccordionGroup
                  icon={<MousePointerClick className="w-[18px] h-[18px] text-[#4B7BFF]" strokeWidth={2} />}
                  title={tw4('sn.interaction.title')}
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <Label htmlFor="redirect-toggle" className="text-sm font-medium text-[#1A1A1A] cursor-pointer">
                          {tw4('sn.interaction.openLink')}
                        </Label>
                        <p className="text-[12px] text-muted-foreground mt-0.5">
                          {tw4('sn.interaction.openLinkDesc')}
                        </p>
                      </div>
                      <Switch
                        id="redirect-toggle"
                        checked={!!salesNotification.redirectUrl || salesNotification.redirectUrl === ''}
                        onCheckedChange={(enabled) => {
                          if (enabled) {
                            updateSalesNotification({ redirectUrl: '' });
                          } else {
                            updateSalesNotification({ redirectUrl: undefined });
                          }
                        }}
                      />
                    </div>
                    
                    <div
                      className={`overflow-hidden transition-[opacity,max-height,margin-top] duration-200 ease-out ${
                        salesNotification.redirectUrl !== undefined
                          ? 'opacity-100 max-h-[120px] mt-3'
                          : 'opacity-0 max-h-0 mt-0'
                      }`}
                    >
                      <div className="space-y-3 pl-0 border-none">
                        <Input
                          value={salesNotification.redirectUrl || ''}
                          onChange={(e) => updateSalesNotification({ redirectUrl: e.target.value })}
                          placeholder={tw4('sn.interaction.urlPlaceholder')}
                          className={cn(
                            "w-full box-border h-11 rounded-xl bg-background border-border/60",
                            redirectUrlState === 'invalid' && "border-red-200 focus-visible:ring-red-200"
                          )}
                        />
                        {/* Mesma validação da Contagem Final: só http/https. Sem
                            esquema ("meusite.com/x") ou protocol-relative
                            ("//meusite.com/x") é normalizado para https:// ao
                            salvar; javascript:/data:/file: e caminho relativo
                            ("/oferta") são recusados pelo backend. */}
                        {redirectUrlState === 'invalid' ? (
                          <p className="text-[11px] text-red-400">
                            {tw4('sn.interaction.urlInvalid')}
                          </p>
                        ) : redirectUrlState === 'autofix' ? (
                          <p className="text-[11px] text-[#64748B]">
                            {tw4('sn.interaction.urlAutofix').replace('{url}', getRedirectUrlAutofixSuffix(salesNotification.redirectUrl))}
                          </p>
                        ) : (
                          <p className="text-[11px] text-[#94A3B8]">
                            {tw4('sn.interaction.urlHelp')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </AccordionGroup>
                
                {/* Grupo 4 — Som (Collapsible) */}
                <AccordionGroup
                  icon={<Volume2 className="w-[18px] h-[18px] text-[#4B7BFF]" strokeWidth={2} />}
                  title={tw4('sn.sound.title')}
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <Label htmlFor="sound-toggle" className="text-[12px] text-muted-foreground cursor-pointer">
                          {tw4('sn.sound.desc')}
                        </Label>
                      </div>
                      <Switch
                        id="sound-toggle"
                        checked={salesNotification.soundEnabled || false}
                        onCheckedChange={(soundEnabled) => updateSalesNotification({ soundEnabled })}
                      />
                    </div>
                    
                    {salesNotification.soundEnabled && (
                      <div className="pl-0 border-none space-y-3">
                        <div className="flex items-center justify-between">
                          <Label className="text-sm text-[#64748B]">{tw4('sn.sound.volume')}</Label>
                          <span className="text-[11px] font-medium text-[#4B7BFF] bg-[#4B7BFF]/[0.08] rounded-full px-2 py-0.5">
                            {salesNotification.soundVolume || 50}%
                          </span>
                        </div>
                        <Slider
                          value={[salesNotification.soundVolume || 50]}
                          min={0}
                          max={100}
                          step={5}
                          onValueChange={([value]) => updateSalesNotification({ soundVolume: value })}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const audio = new Audio('/assets/sounds/cash-register.mp3');
                            audio.volume = (salesNotification.soundVolume || 50) / 100;
                            audio.play().catch(() => {});
                          }}
                          className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#4B7BFF] hover:text-[#4B7BFF]/80 transition-colors"
                        >
                          <Volume2 className="w-3.5 h-3.5" />
                          {tw4('sn.sound.test')}
                        </button>
                      </div>
                    )}
                  </div>
                </AccordionGroup>
                
              </div>
            </TabsContent>
            
            {/* ========== TAB 2 — CONTEÚDO ========== */}
            <TabsContent value="content" className="flex-1 m-0 overflow-y-auto premium-scroll mt-4">
              <div className="space-y-3 pb-6">
                
                {/* Grupo 1 — Mensagem (always open) */}
                <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-[10px] bg-[#EEF4FF] flex items-center justify-center flex-shrink-0">
                      <Package className="w-[18px] h-[18px] text-[#4B7BFF]" strokeWidth={2} />
                    </div>
                    <Label className="text-[14px] font-semibold text-[#1A1A1A]">{tw4('sn.message.title')}</Label>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Nome do produto */}
                    <div className="space-y-2">
                      <Label className="text-sm text-[#64748B]">{tw4('sn.message.product')}</Label>
                      <Input
                        value={salesNotification.product || ''}
                        onChange={(e) => updateSalesNotification({ product: e.target.value })}
                        placeholder={tw4('sn.message.productPlaceholder')}
                        className="h-11 bg-background border-border/60 rounded-xl"
                      />
                    </div>
                    
                    {/* Mostrar plataforma */}
                    <div className="space-y-3 pt-3 border-t border-[#E6E6F0]/60">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="show-platform" className="text-sm font-medium text-[#1A1A1A] cursor-pointer">
                          {tw4('sn.message.showPlatform')}
                        </Label>
                        <Switch
                          id="show-platform"
                          checked={salesNotification.showIcon !== false}
                          onCheckedChange={(showIcon) => updateSalesNotification({ showIcon })}
                        />
                      </div>
                      
                      {salesNotification.showIcon !== false && (
                        <div className="space-y-3 pl-0 border-none">
                          <Select
                            value={salesNotification.platform}
                            onValueChange={handlePlatformChange}
                          >
                            <SelectTrigger className="h-11 bg-white border-[#E6E6F0] rounded-lg hover:border-[#4B7BFF]/40 transition-colors">
                              <SelectValue placeholder={tw4('sn.message.selectPlatform')} />
                            </SelectTrigger>
                            <SelectContent>
                              {SALES_NOTIFICATION_PLATFORMS.map((platform) => (
                                <SelectItem key={platform.value} value={platform.value}>{platform.label}</SelectItem>
                              ))}
                              <SelectItem value="custom">{tw4('sn.message.platformCustom')}</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          {salesNotification.platform === 'custom' && (
                            <div className="mt-3 space-y-2">
                              <Label className="text-[12px] text-[#64748B]">{tw4('sn.message.customLogo')}</Label>
                              <div 
                                className="relative flex items-center justify-center w-full h-20 border-2 border-dashed border-[#E6E6F0] rounded-lg hover:border-[#4B7BFF]/40 transition-colors cursor-pointer bg-white"
                                onClick={() => customIconInputRef.current?.click()}
                              >
                                {salesNotification.customPlatformIcon ? (
                                  <div className="flex items-center gap-3">
                                    <img src={salesNotification.customPlatformIcon} alt={tw4("wx.custom_icon")} className="h-10 w-10 object-contain rounded" />
                                    <span className="text-[12px] text-[#64748B]">{tw4('sn.message.changeLogo')}</span>
                                    <button
                                      type="button"
                                      className="absolute top-1 right-1 p-1 rounded-full hover:bg-[#F1F5F9]"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        updateSalesNotification({ customPlatformIcon: undefined });
                                      }}
                                    >
                                      <X className="w-3 h-3 text-[#94A3B8]" />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex flex-col items-center gap-1">
                                    <ImageIcon className="w-5 h-5 text-[#94A3B8]" />
                                    <span className="text-[11px] text-[#94A3B8]">{tw4('sn.message.addLogo')}</span>
                                  </div>
                                )}
                                <input
                                  ref={customIconInputRef}
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={handleIconUpload}
                                />
                              </div>
                            </div>
                          )}
                          
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Grupo 2 — Dados simulados (Collapsible) */}
                <AccordionGroup
                  icon={<Users className="w-[18px] h-[18px] text-[#4B7BFF]" strokeWidth={2} />}
                  title={tw4('sn.simulated.title')}
                >
                  <div className="space-y-5">
                    {/* Nomes */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium text-[#0F172A]">{tw4('sn.simulated.names')}</Label>
                      <RadioGroup value={namesMode} onValueChange={(v) => setNamesMode(v as 'auto' | 'custom')} className="space-y-2">
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="auto" id="names-auto" />
                          <Label htmlFor="names-auto" className="text-[12px] text-[#64748B] cursor-pointer">
                            {tw4('sn.simulated.auto')} <span className="text-[#94A3B8]">{tw4('sn.simulated.recommended')}</span>
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="custom" id="names-custom" />
                          <Label htmlFor="names-custom" className="text-[12px] text-[#64748B] cursor-pointer">
                            {tw4('sn.simulated.custom')}
                          </Label>
                        </div>
                      </RadioGroup>
                      
                      {namesMode === 'custom' && (
                        <ChipInput
                          items={salesNotification.names}
                          onChange={(names) => updateSalesNotification({ names })}
                          placeholder={tw4('sn.simulated.typeEnter')}
                          mockData={MOCK_NAMES}
                          mockLabel={tw4('sn.simulated.add10')}
                        />
                      )}
                    </div>
                    
                    {/* Cidades */}
                    <div className="space-y-3 pt-4 border-t border-[#E6E6F0]/60">
                      <Label className="text-sm font-medium text-[#0F172A]">{tw4('sn.simulated.cities')}</Label>
                      <RadioGroup value={citiesMode} onValueChange={(v) => {
                        const mode = v as 'auto' | 'custom' | 'none';
                        setCitiesMode(mode);
                        if (mode === 'none') {
                          // Remove {cidade} from template and clear cities
                          const currentTemplate = salesNotification.template || '';
                          const cleanedTemplate = currentTemplate
                            .replace(/\s*de\s*\{cidade\}/gi, '')
                            .replace(/\s*de\s*\{Cidade\}/g, '')
                            .replace(/\s*\{cidade\}/gi, '')
                            .replace(/\s*\{Cidade\}/g, '');
                          updateSalesNotification({ cities: [], template: cleanedTemplate });
                        } else if (mode === 'custom' || mode === 'auto') {
                          // Restore {cidade} in template if not present
                          const currentTemplate = salesNotification.template || '';
                          if (!currentTemplate.includes('{cidade}') && !currentTemplate.includes('{Cidade}')) {
                            const restoredTemplate = currentTemplate.replace(
                              /(\{nome\}|\{Nome\})/i, 
                              '$1 de {cidade}'
                            );
                            updateSalesNotification({ 
                              template: restoredTemplate,
                              ...(mode === 'custom' && salesNotification.cities.length === 0 ? { cities: ['São Paulo', 'Rio de Janeiro', 'Belo Horizonte', 'Brasília', 'Salvador'] } : {})
                            });
                          }
                        }
                      }} className="space-y-2">
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="auto" id="cities-auto" />
                          <Label htmlFor="cities-auto" className="text-[12px] text-[#64748B] cursor-pointer">
                            {tw4('sn.simulated.auto')} <span className="text-[#94A3B8]">{tw4('sn.simulated.recommended')}</span>
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="custom" id="cities-custom" />
                          <Label htmlFor="cities-custom" className="text-[12px] text-[#64748B] cursor-pointer">
                            {tw4('sn.simulated.custom')}
                          </Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem value="none" id="cities-none" />
                          <Label htmlFor="cities-none" className="text-[12px] text-[#64748B] cursor-pointer">
                            {tw4('sn.simulated.noLocation')}
                          </Label>
                        </div>
                      </RadioGroup>
                      
                      {citiesMode === 'custom' && (
                        <ChipInput
                          items={salesNotification.cities}
                          onChange={(cities) => updateSalesNotification({ cities })}
                          placeholder={tw4('sn.simulated.typeEnter')}
                          mockData={MOCK_CITIES}
                          mockLabel={tw4('sn.simulated.add10')}
                        />
                      )}
                    </div>
                  </div>
                </AccordionGroup>
                
                {/* Grupo 3 — Template (Collapsible) */}
                <AccordionGroup
                  icon={<Type className="w-[18px] h-[18px] text-[#4B7BFF]" strokeWidth={2} />}
                  title={tw4('sn.template.title')}
                >
                  <div className="space-y-3">
                    <Textarea
                      value={salesNotification.template || ''}
                      onChange={(e) => handleTemplateChange(e.target.value)}
                      placeholder={tw4('sn.template.placeholder')}
                      className="min-h-[56px] bg-background border-border/60 rounded-xl resize-none"
                      rows={2}
                    />
                    
                    {/* Variable chips — Azul Host */}
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-[#94A3B8]">{tw4('sn.template.insert')}</span>
                      <div className="flex gap-1.5 flex-wrap">
                        {['{nome}', '{cidade}', '{produto}', '{plataforma}'].map((variable) => (
                          <button
                            key={variable}
                            type="button"
                            onClick={() => handleTemplateChange((salesNotification.template || '') + ' ' + variable)}
                            className="px-2.5 py-1 text-[11px] font-medium bg-[#4B7BFF]/[0.08] text-[#4B7BFF] rounded-lg hover:bg-[#4B7BFF]/[0.15] transition-colors"
                          >
                            {variable}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Preview */}
                    {salesNotification.template && (
                      <div className="p-3 bg-[#F8FAFC] rounded-xl border border-[#E4E7EC]">
                        <div className="flex items-start gap-2">
                          <Eye className="w-4 h-4 text-[#4B7BFF] flex-shrink-0 mt-0.5" strokeWidth={2} />
                          <div className="flex-1">
                            <p className="text-[10px] font-medium text-[#94A3B8] uppercase tracking-wide mb-1">{tw4('sn.template.preview')}</p>
                            <p className="text-[13px] text-[#0F172A] leading-relaxed">
                              {salesNotification.template
                                .replace(/{nome}/gi, salesNotification.names?.[0] || '[Nome]')
                                .replace(/{cidade}/gi, salesNotification.cities?.[0] || '[Cidade]')
                                .replace(/{produto}/gi, salesNotification.product || '[Produto]')
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </AccordionGroup>
                
              </div>
            </TabsContent>
            
            {/* ========== TAB 3 — APARÊNCIA ========== */}
            <TabsContent value="appearance" className="flex-1 m-0 overflow-y-auto premium-scroll mt-4">
              <div className="space-y-3 pb-6">
                
                {/* Grupo 1 — Cores e Estilo */}
                <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-[10px] bg-[#EEF4FF] flex items-center justify-center flex-shrink-0">
                      <Palette className="w-[18px] h-[18px] text-[#4B7BFF]" strokeWidth={2} />
                    </div>
                    <Label className="text-[14px] font-semibold text-[#1A1A1A]">{tw4('sn.appearance.title')}</Label>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Cor de Fundo */}
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium text-[#64748B]">{tw4('sn.appearance.bg')}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="w-8 h-8 rounded-lg border border-[#E4E7EC] hover:border-[#4B7BFF]/40 transition-colors" style={{ backgroundColor: salesNotification.style.bgColor || '#ffffff' }} />
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-3" align="end">
                          <HexColorPicker color={salesNotification.style.bgColor || '#ffffff'} onChange={(c) => updateSalesNotification({ style: { ...salesNotification.style, bgColor: c } })} />
                          <Input value={salesNotification.style.bgColor || '#ffffff'} onChange={(e) => updateSalesNotification({ style: { ...salesNotification.style, bgColor: e.target.value } })} className="mt-2 h-8 text-xs font-mono" />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Cor da Borda */}
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium text-[#64748B]">{tw4('sn.appearance.border')}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="w-8 h-8 rounded-lg border border-[#E4E7EC] hover:border-[#4B7BFF]/40 transition-colors" style={{ backgroundColor: salesNotification.style.borderColor || '#eeeeee' }} />
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-3" align="end">
                          <HexColorPicker color={salesNotification.style.borderColor || '#eeeeee'} onChange={(c) => updateSalesNotification({ style: { ...salesNotification.style, borderColor: c } })} />
                          <Input value={salesNotification.style.borderColor || '#eeeeee'} onChange={(e) => updateSalesNotification({ style: { ...salesNotification.style, borderColor: e.target.value } })} className="mt-2 h-8 text-xs font-mono" />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Cor da Letra */}
                    <div className="flex items-center justify-between">
                      <Label className="text-xs font-medium text-[#64748B]">{tw4('sn.appearance.color')}</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <button className="w-8 h-8 rounded-lg border border-[#E4E7EC] hover:border-[#4B7BFF]/40 transition-colors" style={{ backgroundColor: salesNotification.style.textColor || '#333333' }} />
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-3" align="end">
                          <HexColorPicker color={salesNotification.style.textColor || '#333333'} onChange={(c) => updateSalesNotification({ style: { ...salesNotification.style, textColor: c } })} />
                          <Input value={salesNotification.style.textColor || '#333333'} onChange={(e) => updateSalesNotification({ style: { ...salesNotification.style, textColor: e.target.value } })} className="mt-2 h-8 text-xs font-mono" />
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Arredondamento */}
                    <div className="space-y-3 pt-3 border-t border-[#E6E6F0]/60">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-medium text-[#64748B]">{tw4('sn.appearance.radius')}</Label>
                        <span className="text-xs font-semibold text-[#4B7BFF] bg-[#4B7BFF]/[0.08] px-2.5 py-1 rounded-full">
                          {salesNotification.style.borderRadius || 16}px
                        </span>
                      </div>
                      <Slider
                        value={[salesNotification.style.borderRadius || 16]}
                        min={0}
                        max={24}
                        step={2}
                        onValueChange={([value]) => updateSalesNotification({ style: { ...salesNotification.style, borderRadius: value } })}
                      />
                    </div>
                  </div>
                  
                  {/* Show icon toggle */}
                  <div className="flex items-center justify-between pt-3 mt-4 border-t border-[#E6E6F0]/60">
                    <Label htmlFor="show-icon-appearance" className="text-sm text-[#64748B]">
                      {tw4('sn.appearance.showIcon')}
                    </Label>
                    <Switch
                      id="show-icon-appearance"
                      checked={salesNotification.showIcon !== false}
                      onCheckedChange={(showIcon) => updateSalesNotification({ showIcon })}
                    />
                  </div>
                </div>
                
                {/* Grupo 2 — Ajustes rápidos (Collapsible) */}
                <AccordionGroup
                  icon={<Zap className="w-[18px] h-[18px] text-[#4B7BFF]" strokeWidth={2} />}
                  title={tw4('sn.quick.title')}
                >
                  <div className="space-y-5">
                    {/* Tamanho unificado — Azul Host */}
                    <div className="space-y-3">
                      <Label className="text-xs font-semibold text-[#0F172A]">{tw4('sn.quick.size')}</Label>
                      <div className="flex gap-2">
                        {(['small', 'medium', 'large'] as const).map((size) => {
                          const labels = { small: tw4('sn.size.small'), medium: tw4('sn.size.medium'), large: tw4('sn.size.large') };
                          const isSelected = currentSize === size;
                          return (
                            <button
                              key={size}
                              type="button"
                              onClick={() => updateSalesNotification({ fontSize: size, iconSize: size })}
                              className={cn(
                                "flex-1 h-9 rounded-lg text-[12px] font-medium transition-all",
                                isSelected
                                  ? "bg-[#4B7BFF] text-white shadow-sm"
                                  : "bg-[#F1F5F9] text-[#64748B] hover:bg-[#E2E8F0]"
                              )}
                            >
                              {labels[size]}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    
                  </div>
                </AccordionGroup>
                
                {/* Micro copy */}
                <p className="text-[11px] text-[#94A3B8] text-center mt-4 px-4">
                  {tw4('sn.disclaimer')}
                </p>
                
              </div>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
};
